import React, { useState, useEffect, useCallback } from "react";
import { Image as ImageIcon, Upload, X, Loader2, Sparkles } from "lucide-react";
import { supabase } from "../lib/supabase";

/* ── Tokens — identical to ProDashboardHome / ProSettingsPanel ── */
const T = {
  green: "#1e9254", greenDark: "#155c36", greenTint: "#eef8f3", greenBd: "#a7d9bc",
  border: "#e4e7ef", borderFaint: "#eef0f4", card2: "#f7f8fa",
  text: "#0f1117", textMid: "#2d3549", sub: "#4a5268", faint: "#8b93a7", ghost: "#c7cddb",
  red: "#b3261e", redTint: "#fdf3f2", redBd: "#f0c9c5",
  font: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  serif: "'Instrument Serif', Georgia, serif",
  r8: "8px", r10: "10px", r12: "12px", r14: "14px", r16: "16px", rFull: "999px",
  s1: "0 1px 3px rgba(13,46,26,0.05)", s2: "0 4px 16px rgba(13,46,26,0.07)",
  sGreen: "0 6px 20px rgba(30,146,84,0.22)",
  tx: "all 0.18s cubic-bezier(.4,0,.2,1)",
};

const MAX_IMAGES = 12;
const MAX_FILE_MB = 8;

if (typeof document !== "undefined" && !document.getElementById("__pm_fonts")) {
  const l = document.createElement("link");
  l.id = "__pm_fonts";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
  document.head.appendChild(l);
}
if (typeof document !== "undefined" && !document.getElementById("__pm_styles")) {
  const s = document.createElement("style");
  s.id = "__pm_styles";
  s.textContent = `
    @keyframes pmSpin { to { transform: rotate(360deg); } }
    @keyframes pmPulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
    .pm-tile { position:relative; border-radius:${T.r10}; overflow:hidden; border:1.5px solid ${T.border}; transition:${T.tx}; }
    .pm-tile:hover { border-color:${T.greenBd}; box-shadow:${T.s2}; transform:translateY(-1px); }
    .pm-tile:hover .pm-tile-overlay { opacity:1; }
    .pm-tile-overlay { position:absolute; inset:0; background:linear-gradient(to top, rgba(13,17,23,0.55), transparent 55%); opacity:0; transition:${T.tx}; pointer-events:none; }
    .pm-del-btn { transition:${T.tx}; }
    .pm-del-btn:hover { background:${T.red} !important; }
    .pm-upload-pill:hover { background:${T.greenDark} !important; }
  `;
  document.head.appendChild(s);
}

async function fetchPortfolio(proId) {
  const { data, error } = await supabase
    .from("portfolio_images")
    .select("id, url, caption, sort_order")
    .eq("pro_id", proId)
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return data || [];
}

async function uploadPortfolioImage(proId, file) {
  const ext = file.name.split(".").pop();
  const path = `${proId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const { error: uploadErr } = await supabase.storage.from("portfolio-images").upload(path, file);
  if (uploadErr) throw uploadErr;
  const { data: pub } = supabase.storage.from("portfolio-images").getPublicUrl(path);
  const { data, error } = await supabase
    .from("portfolio_images")
    .insert({ pro_id: proId, url: pub.publicUrl, sort_order: Date.now() })
    .select()
    .single();
  if (error) throw error;
  return data;
}

async function deletePortfolioImage(id, url) {
  try {
    const path = url.split("/portfolio-images/")[1];
    if (path) await supabase.storage.from("portfolio-images").remove([path]);
  } catch { /* non-fatal */ }
  const { error } = await supabase.from("portfolio_images").delete().eq("id", id);
  if (error) throw error;
}

export default function PortfolioManager({ proId }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const load = useCallback(() => {
    if (!proId) return;
    setLoading(true);
    fetchPortfolio(proId)
      .then(d => { setImages(d); setError(null); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [proId]);

  useEffect(() => { load(); }, [load]);

  const handleFiles = async (files) => {
    setUploadError(null);
    const list = Array.from(files).slice(0, MAX_IMAGES - images.length);
    if (list.length === 0) { setUploadError(`You can upload up to ${MAX_IMAGES} photos.`); return; }
    setUploading(true);
    try {
      for (const file of list) {
        if (!file.type.startsWith("image/")) continue;
        if (file.size > MAX_FILE_MB * 1024 * 1024) { setUploadError(`${file.name} is over ${MAX_FILE_MB}MB.`); continue; }
        const row = await uploadPortfolioImage(proId, file);
        setImages(prev => [...prev, row]);
      }
    } catch (e) { setUploadError(e.message); } finally { setUploading(false); }
  };

  const handleDelete = async (img) => {
    setImages(prev => prev.filter(i => i.id !== img.id));
    try { await deletePortfolioImage(img.id, img.url); } catch (e) { setError(e.message); load(); }
  };

  const pct = Math.min(100, Math.round((images.length / MAX_IMAGES) * 100));

  return (
    <div style={{ background: "#fff", borderRadius: T.r16, border: `1.5px solid ${T.border}`, boxShadow: T.s1, overflow: "hidden", fontFamily: T.font }}>
      <div style={{ padding: "20px 22px", borderBottom: `1px solid ${T.borderFaint}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ width: 36, height: 36, borderRadius: "50%", background: T.greenTint, border: `1px solid ${T.greenBd}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <ImageIcon size={16} color={T.green} strokeWidth={1.8} />
          </span>
          <div>
            <p style={{ margin: "0 0 3px", fontSize: 10, fontWeight: 700, color: T.green, textTransform: "uppercase", letterSpacing: "0.09em", fontFamily: T.font }}>Shown on your public profile</p>
            <div style={{ fontSize: 19, fontWeight: 400, fontStyle: "italic", color: T.text, fontFamily: T.serif, letterSpacing: "-0.01em" }}>Portfolio photos</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
              <div style={{ width: 60, height: 5, borderRadius: 99, background: T.borderFaint, overflow: "hidden" }}>
                <div style={{ width: `${pct}%`, height: "100%", background: T.green, borderRadius: 99, transition: T.tx }} />
              </div>
              <span style={{ fontSize: 11.5, color: T.faint, fontFamily: T.font, fontWeight: 600 }}>{images.length}/{MAX_IMAGES}</span>
            </div>
          </div>
        </div>
        <label className="pm-upload-pill" style={{
          display: "flex", alignItems: "center", gap: 6, padding: "11px 20px",
          background: uploading ? T.greenTint : T.green, color: uploading ? T.green : "#fff",
          borderRadius: T.rFull, fontSize: 13, fontWeight: 700, cursor: uploading ? "default" : "pointer",
          fontFamily: T.font, transition: T.tx, boxShadow: uploading ? "none" : T.sGreen,
        }}>
          {uploading ? <Loader2 size={14} style={{ animation: "pmSpin 0.8s linear infinite" }} /> : <Upload size={14} />}
          {uploading ? "Uploading…" : "Add photos"}
          <input type="file" accept="image/*" multiple hidden disabled={uploading}
            onChange={e => { handleFiles(e.target.files); e.target.value = ""; }} />
        </label>
      </div>

      {uploadError && (
        <div style={{ margin: "16px 22px 0", padding: "11px 14px", background: T.redTint, border: `1px solid ${T.redBd}`, borderRadius: T.r10, fontSize: 12.5, color: T.red, fontFamily: T.font, display: "flex", alignItems: "center", gap: 8 }}>
          <X size={14} strokeWidth={2} /> {uploadError}
        </div>
      )}

      <div style={{ padding: 22 }}>
        {loading ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 10 }}>
            {[0,1,2,3].map(i => <div key={i} style={{ aspectRatio: "1", borderRadius: T.r10, background: T.card2, animation: "pmPulse 1.4s ease-in-out infinite" }} />)}
          </div>
        ) : error ? (
          <div style={{ fontSize: 13, color: T.red, fontFamily: T.font }}>{error}</div>
        ) : images.length === 0 ? (
          <div style={{ textAlign: "center", padding: "38px 10px", border: `1.5px dashed ${T.border}`, borderRadius: T.r14 }}>
            <div style={{ width: 46, height: 46, borderRadius: "50%", background: T.greenTint, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
              <ImageIcon size={20} color={T.green} strokeWidth={1.6} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.font }}>No photos yet</div>
            <div style={{ fontSize: 12.5, color: T.sub, fontFamily: T.font, marginTop: 5, maxWidth: 280, margin: "5px auto 0", lineHeight: 1.6 }}>
              Add before/after shots of your work. Profiles with 5+ photos get noticeably more bookings.
            </div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 10 }}>
            {images.map(img => (
              <div key={img.id} className="pm-tile" style={{ aspectRatio: "1" }}>
                <img src={img.url} alt={img.caption || ""} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                <div className="pm-tile-overlay" />
                <button onClick={() => handleDelete(img)} className="pm-del-btn" style={{
                  position: "absolute", top: 6, right: 6, width: 24, height: 24, borderRadius: "50%",
                  background: "rgba(15,17,23,0.62)", border: "none", display: "flex", alignItems: "center",
                  justifyContent: "center", cursor: "pointer",
                }}>
                  <X size={12} color="#fff" strokeWidth={2.2} />
                </button>
              </div>
            ))}
            {images.length < MAX_IMAGES && (
              <label className="pm-tile" style={{
                aspectRatio: "1", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                gap: 6, cursor: uploading ? "default" : "pointer", border: `1.5px dashed ${T.greenBd}`, background: T.greenTint,
              }}>
                <Sparkles size={16} color={T.green} strokeWidth={1.8} />
                <span style={{ fontSize: 10.5, fontWeight: 700, color: T.green, fontFamily: T.font }}>Add more</span>
                <input type="file" accept="image/*" multiple hidden disabled={uploading}
                  onChange={e => { handleFiles(e.target.files); e.target.value = ""; }} />
              </label>
            )}
          </div>
        )}
      </div>
    </div>
  );
}